import { tiposDeFotos } from "@/constants/tipoFoto.js";
import { db, storageInst, storageMolduras } from "@/service/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadString } from "firebase/storage";

export async function getFotosByCompraId(compraId) {
  const FotosRef = collection(db, "fotos");
  const searchQuery = query(FotosRef, where("compra_id", "==", `${compraId}`));
  const querySnaps = await getDocs(searchQuery);
  const fotos = [];
  querySnaps.forEach((item) => {
    const bin = item.data();
    fotos.push({
      id: item.id,
      url_editada: bin.url_editada,
      url_original: bin.url_original,
      compra_id: bin.compra_id,
    });
  });
  return fotos;
}

// Só as molduras ativas do tipo da compra. Antes a consulta dos tipos não
// polaroid filtrava apenas "ativo": vinham as molduras de todos os tipos, e o
// editor resolvia a URL de cada uma para depois descartar as de outro tipo
// (em 3x4 e passaporte, todas).
export async function getMoldurasByTipo(tipo) {
  const referencia = collection(db, "/molduras");
  const filtros = [where("tipo", "==", tipo), where("ativo", "==", true)];

  if (tipo == tiposDeFotos.POLAROID) {
    // Apenas molduras Polaroid v2 (600x800); as legadas (500x600) não
    // encaixam no canvas atual.
    filtros.push(where("polaroid_edit_parte_branca", "==", true));
  }

  const moldurasList = await getDocs(query(referencia, ...filtros));
  const molduras = [];
  moldurasList.forEach((snap) => {
    const dados = snap.data();
    // Defesa caso o cache offline do Firestore devolva algo fora do filtro.
    if (dados.ativo !== true || dados.tipo !== tipo) return;
    molduras.push({
      dados,
      id: snap.id,
    });
  });
  return molduras;
}

export function filtrarMoldurasIgnoradas(molduras, moldurasIgnoradas) {
  if (!Array.isArray(moldurasIgnoradas)) return molduras;
  return molduras.filter(
    (snap) =>
      snap.dados?.global === true || !moldurasIgnoradas.includes(snap.id)
  );
}

/** Molduras do modo evento ficam no documento de personalização do Totem. */
export async function getMoldurasEventoByTipo(totemId, tipo, now = Date.now()) {
  if (!totemId) return [];
  const snapshot = await getDoc(doc(db, "personalizaTotem", totemId));
  const molduras = snapshot.exists() ? snapshot.data()?.molduras : [];
  if (!Array.isArray(molduras)) return [];
  return molduras
    .filter((item) => {
      if (!item || item.tipo !== tipo || typeof item.url !== "string")
        return false;
      if (!/^https:\/\//.test(item.url)) return false;
      if (!item.expiraEm) return true;
      const expiration = Date.parse(item.expiraEm);
      return Number.isFinite(expiration) && expiration > now;
    })
    .map((item) => {
      const nome = String(item.nome || "Moldura do evento");
      const orientacao =
        tipo === tiposDeFotos.POLAROID
          ? "RETRATO"
          : item.orientacao === "PAISAGEM"
          ? "PAISAGEM"
          : "RETRATO";
      return {
        id: String(item.id),
        descricao: nome,
        dados: {
          descricao: nome,
          tipo,
          orientacao,
          url: item.url,
          ativo: true,
          evento: true,
          eventoId: String(item.id),
          expiraEm: item.expiraEm || null,
        },
      };
    });
}

export async function resolveStorageUrls(referencias) {
  const results = [];
  for (const item of referencias) {
    const itemRefStorage = ref(storageInst, `${item.url_original}`);
    const url = await getDownloadURL(itemRefStorage);
    results.push({
      src: url,
      refOriginal: item.url_original,
      refEdicao: item.url_editada,
    });
  }
  return results;
}

export async function resolveMolduraStorageUrl(item) {
  const filename = item.dados.referenciaStorage;
  const refStorage = ref(storageMolduras, filename);
  const link = await getDownloadURL(refStorage);
  return link;
}

// `tempoMaximoMs`: limita as novas tentativas internas do SDK neste envio. O
// padrão do Firebase é 10 minutos tentando por conta própria — com a internet
// caindo e voltando, o totem ficava esse tempo todo na tela de carregando.
export async function uploadImageDataURL(
  path,
  dataURI,
  { tempoMaximoMs } = {}
) {
  const storageRef = ref(storageInst, `${path}`);
  if (!tempoMaximoMs) {
    await uploadString(storageRef, dataURI, "data_url");
    return;
  }
  // O SDK lê o limite ao montar a requisição, de forma síncrona dentro do
  // uploadString: troca só para este envio e devolve na hora, sem afetar os
  // outros uploads do app (ex.: fotos da selfie).
  const limiteAnterior = storageInst.maxUploadRetryTime;
  let envio;
  try {
    storageInst.maxUploadRetryTime = tempoMaximoMs;
    envio = uploadString(storageRef, dataURI, "data_url");
  } finally {
    storageInst.maxUploadRetryTime = limiteAnterior;
  }
  await envio;
}
